import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, onBeforeUnmount, computed, watch } from "vue";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import { IBusStopData } from "@/domain/entities/busStop";
import MapZoomLevelSetter from "@/components/map/MapZoomLevelSetter.vue";
import InlineSvg from "vue-inline-svg";
import SwiperCore, { Navigation, Pagination } from "swiper";
import "swiper/swiper.scss";
import { nextTick } from "process";
import Page from "@/components/shared/Page.vue";
import BusStopStreetView from "@/components/map/BusStopStreetView.vue";
import { IBusLocation, ILineInfoLite } from "@/domain/entities/lineInfo";
import { useLineStore } from "@/stores/lineStore";
import { useRoute } from "vue-router";
import { useNaverMapPaths } from "@/composables/map/useNaverMapPaths";
import {
  BUS_LOCATION_REFRESH_TIME_MILLIS,
  LineTypeCode,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  ShowErrorModal,
  ShowToastMessage,
} from "@/types";
import { useNaverMap } from "@/composables/map/useNaverMap";
import { useCreateBusStopMarkers } from "@/composables/map/useCreateBusStopMarkers";
import { useBusLocationMarkers } from "@/composables/map/useBusLocationMarkers";
import { useZoomLevel } from "@/composables/map/useZoomLevel";
import RoadEventListModal from "@/components/line/roadEvent/RoadEventListModal.vue";
import useGetLineRouteName from "@/use/useGetLineRouteName";
import { setIntervalHandler } from "@/utils/setIntervalHandler";
import { useServiceStore } from "@/stores/serviceStore";
import { useBusLocationDrivingStateManager } from "@/composables/line/useBusLocationDrivingStateManager";
import { useBusLocationFetchConfig } from "@/composables/line/useBusLocationFetchConfig";
import { inject } from "vue";
import dayjs from "dayjs";
import { useShouldGetBusLocation } from "@/composables/map/useShouldGetBusLocation";
import PageModal from "@/components/shared/modal/base/PageModal.vue";
import getBusStopsByDate from "@/use/useGetBusStopsByDate";
import getPathsByDate from "@/use/useGetPathsByDate";
import MapOfNearbyBusStopsBottomSheet from "./MapOfNearbyBusStopsBottomSheet.vue";


const __default__ = {
  name: "MapOfLineBusStops",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  emits: ["onClickClose"],
  setup(__props, { expose: __expose }) {
  __expose();



const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;

const serviceStore = useServiceStore();
const lineStore = useLineStore();

const route = useRoute();
const windowHeight = window.innerHeight;

const turnId = route.params.turnId?.toString() ?? "";
const busStopId = route.params.busStopId?.toString() ?? "";
const targetDateTime = decodeURIComponent(
  route.query.targetDateTime?.toString() ?? ""
);

const lineInfo = lineStore.selectedLineInfoDetail;

const title = computed(() => {
  const lineRouteName = lineInfo
    ? useGetLineRouteName(
        lineInfo.lineTypeCode,
        lineInfo.routeName,
        lineInfo.lineNum
      )
    : "-";
  return `${lineRouteName} 정류장`;
});

const turn = lineInfo?.turns.find((turn) => turn.id === turnId);
const turnPaths = turn ? getPathsByDate(turn, targetDateTime) : [];
const detourPaths = turn?.detourPaths ?? [];

const busStops = turn ? getBusStopsByDate(turn, targetDateTime) : [];
const selectedBusStop = ref<IBusStopData | undefined>(
  lineStore.getBusStopById(busStopId.toString())
);

const initalIndex = computed(() => {
  return busStops.findIndex((busStop) => busStop.id === busStopId);
});

const bottomSheetModal =
  ref<InstanceType<typeof MapOfNearbyBusStopsBottomSheet>>();

/** 지도 Zoom Level */
const DEFAULT_ZOOM_LEVEL = 15;
const MIN_ZOOM_LEVEL = 8;
const MAX_ZOOM_LEVEL = 18;

const {
  zoomLevel,
  increaseZoomLevel,
  decreaseZoomLevel,
  addZoomChangedNaverEventListener,
  removeZoomChangedNaverEventListener,
} = useZoomLevel({
  initialZoomLevel: DEFAULT_ZOOM_LEVEL,
  minZoom: MIN_ZOOM_LEVEL,
  maxZoom: MAX_ZOOM_LEVEL,
});

const { map, addScript, initMap, setCenter } = useNaverMap(
  selectedBusStop.value?.latitude ?? 37.2529797,
  selectedBusStop.value?.longitude ?? 127.4806853,
  "#707070",
  DEFAULT_ZOOM_LEVEL,
  MIN_ZOOM_LEVEL,
  MAX_ZOOM_LEVEL
);

// NavigationBar 아래 10px 위치로 maxHeight 계산
const maxHeightPx = computed(() => {
  const statusBarHeight = parseInt(
    getComputedStyle(document.documentElement).getPropertyValue(
      "--statusbar-height"
    ) || "0"
  );
  const navHeight = 48; // $navHeight 값
  return windowHeight - (statusBarHeight + navHeight + 10);
});

/** 버스 정류장 마커 생성 */
const { createBusStopMarkers, updateSelectedBusStopMarker } =
  useCreateBusStopMarkers();

/** 버스 실시간 위치 마커 생성 */
const { createBusLocationMarkers, removeBusLocationMarkers } =
  useBusLocationMarkers();

const { drawNaverMapPaths } = useNaverMapPaths();

const createNaverMapIconAndMarker = () => {
  createBusStopMarkers(map.value, busStops);
  updateSelectedBusStopMarker(selectedBusStop.value?.id);
  createBusLocationMarkers(map.value, busLocations.value);
  drawNaverMapPaths(map.value, {
    paths: detourPaths.length ? detourPaths : turnPaths,
  });
};

const updateBusStopMarkerWithCenter = (shouldUpdateCenter: boolean) => {
  if (!map.value || !selectedBusStop.value) return;

  // 선택된 버스정류장만 변경되면 마커 스타일 업데이트 및 중심 이동
  updateSelectedBusStopMarker(selectedBusStop.value?.id);
  if (shouldUpdateCenter) {
    setCenter(selectedBusStop.value.latitude, selectedBusStop.value.longitude);
  }
};

const updateSelectedBusStop = (
  busStop?: IBusStopData,
  shouldUpdateMapCenter = true
) => {
  if (!busStop) return;
  selectedBusStop.value = busStop;
  nextTick(() => {
    updateBusStopMarkerWithCenter(shouldUpdateMapCenter);
  });
};

const lineInfos = computed<ILineInfoLite[]>(() => {
  if (!selectedBusStop.value) {
    return [];
  }
  return lineStore.getLineInfosLiteByBusStopId(selectedBusStop.value.id);
});

const movePreviousBusStop = () => {
  bottomSheetModal.value?.movePreviousBusStop();
};

const moveNextBusStop = () => {
  bottomSheetModal.value?.moveNextBusStop();
};

// StreetView
const shouldShowStreetView = ref(false);
const toggleShowStreetView = (show: boolean) => {
  if (show && !selectedBusStop.value) return;

  shouldShowStreetView.value = show;
};

/** 실시간 버스 위치 API Polling */
const busLocationUpdateIntervalMillis = computed(
  () =>
    serviceStore.serviceInfo?.busLocationUpdateIntervalMillis ||
    BUS_LOCATION_REFRESH_TIME_MILLIS
);

const { setIntervalCallback, clearIntervalCallback } = setIntervalHandler(
  busLocationUpdateIntervalMillis.value
);

/** 모든 호차에 대한 위치정보 가져오기 */
const busLocations = ref<IBusLocation[]>([]);

/** 버스 위치 정보 API의 응답값을 보고, 버스의 주행 상태 파악 */
const {
  updateHasAllBusesArrivedLastStop,
  updateHasAllBusesEndedDriving,

  isBusRunning,
  updateBusRunningStatus,
  shouldGetBusLocation,
} = useBusLocationDrivingStateManager();

const {
  isLoggedIn,
  isLocationCacheExpired,
  lastBusStopArrivalThresholdMin,
  updateBusLocationUpdatedAt,
} = useBusLocationFetchConfig();

const isInsideShuttle = computed(
  () => lineInfo?.lineTypeCode === LineTypeCode.Inside
);

const getLastBusStop = () => {
  // 종점 정류장
  return isInsideShuttle.value
    ? lineInfo?.turns[lineInfo.turns.length - 1].lastStop
    : turn?.lastStop;
};

const getFirstBusStop = () => {
  // 기점 정류장
  return isInsideShuttle.value ? lineInfo?.turns[0].firstStop : turn?.firstStop;
};

/** 실시간 버스 위치 API 호출 */
const getBusLocations = async () => {
  if (!lineInfo) return;

  const result = await shouldGetBusLocation(
    isLoggedIn.value,
    lastBusStopArrivalThresholdMin.value,
    lineInfo.lineTypeCode,
    lineInfo.campusName,
    getFirstBusStop(),
    getLastBusStop()
  );

  if (!result.shouldGetLocation) {
    busLocations.value = [];
    isBusRunning.value = false;
    return;
  }

  if (!isLocationCacheExpired()) {
    return;
  }

  const busLocationResult = await lineStore.getBusLocations(
    lineInfo?.id ?? "",
    isInsideShuttle.value ? undefined : turn?.id
  );

  updateBusLocationUpdatedAt(dayjs());
  busLocations.value = busLocationResult.data ?? [];
  updateHasAllBusesArrivedLastStop(busLocations.value);
  updateHasAllBusesEndedDriving(busLocations.value);
  updateBusRunningStatus(busLocations.value, result.shouldGetLocation);
};

// 현재 버스 위치가 없는 경우, 버스 위치 확인 불가 토스트메시지 표출
const showToastIfNoBusLocations = () => {
  if (busLocations.value.length === 0) {
    showToastMessage("현재 운행중인 버스 정보를 찾을 수 없습니다");
  }
};

const refreshButton = ref();
const isRotating = ref(false);

const onClickRefreshBtn = async () => {
  if (isRotating.value) return;
  isRotating.value = true;

  setTimeout(() => {
    isRotating.value = false;
  }, 500);

  await getBusLocations();
  showToastIfNoBusLocations();
};

// 교통정보 모달
const shouldShowRoadEventListModal = ref(false);
const toggleShowRoadEventListModal = (show: boolean) => {
  shouldShowRoadEventListModal.value = show;
};

const isSameBusLocation = (
  newLocations: IBusLocation[],
  oldLocations?: IBusLocation[]
) => {
  if (!oldLocations || newLocations.length !== oldLocations.length)
    return false;

  return newLocations.every((newLocation, index) => {
    const targetOldLocation = oldLocations?.[index];
    return (
      newLocation.lat === targetOldLocation?.lat &&
      newLocation.lng === targetOldLocation?.lng
    );
  });
};

watch(
  () => busLocations.value,
  (newBusLocations, oldBusLocations) => {
    if (!map.value) return;
    if (isSameBusLocation(newBusLocations, oldBusLocations)) return;
    removeBusLocationMarkers();
    createBusLocationMarkers(map.value, newBusLocations);
  }
);

const createNaverMap = () => {
  window.naver ? initMap() : addScript();
};

watch(map, (newMap: any) => {
  if (newMap) {
    addZoomChangedNaverEventListener(map.value);
    createNaverMapIconAndMarker();
    return;
  }

  removeZoomChangedNaverEventListener();
});

const { shouldGetBusLocationAndFindCampus } = useShouldGetBusLocation();
const shouldShowLocationRefreshButton = ref(true);

const handleBusLocation = async () => {
  if (!lineInfo?.lineTypeCode) {
    await setIntervalCallback(getBusLocations, true);
    showToastIfNoBusLocations();
    return;
  }

  const result = await shouldGetBusLocationAndFindCampus(
    lineInfo.lineTypeCode,
    lineInfo.campusName
  );
  if (!result.shouldGetLocation && !result.nearbyCampusName) {
    shouldShowLocationRefreshButton.value = false;
    showErrorModal(
      "안내",
      `${lineInfo.campusName} 내에서만 실시간 버스 위치를 조회할 수 있습니다.`
    );
    return;
  }

  await setIntervalCallback(getBusLocations, true);
  showToastIfNoBusLocations();
};

onMounted(async () => {
  createNaverMap();
  await handleBusLocation();
});

onBeforeUnmount(() => {
  clearIntervalCallback();
});

const __returned__ = { showErrorModal, showToastMessage, serviceStore, lineStore, route, windowHeight, turnId, busStopId, targetDateTime, lineInfo, title, turn, turnPaths, detourPaths, busStops, selectedBusStop, initalIndex, bottomSheetModal, DEFAULT_ZOOM_LEVEL, MIN_ZOOM_LEVEL, MAX_ZOOM_LEVEL, zoomLevel, increaseZoomLevel, decreaseZoomLevel, addZoomChangedNaverEventListener, removeZoomChangedNaverEventListener, map, addScript, initMap, setCenter, maxHeightPx, createBusStopMarkers, updateSelectedBusStopMarker, createBusLocationMarkers, removeBusLocationMarkers, drawNaverMapPaths, createNaverMapIconAndMarker, updateBusStopMarkerWithCenter, updateSelectedBusStop, lineInfos, movePreviousBusStop, moveNextBusStop, shouldShowStreetView, toggleShowStreetView, busLocationUpdateIntervalMillis, setIntervalCallback, clearIntervalCallback, busLocations, updateHasAllBusesArrivedLastStop, updateHasAllBusesEndedDriving, isBusRunning, updateBusRunningStatus, shouldGetBusLocation, isLoggedIn, isLocationCacheExpired, lastBusStopArrivalThresholdMin, updateBusLocationUpdatedAt, isInsideShuttle, getLastBusStop, getFirstBusStop, getBusLocations, showToastIfNoBusLocations, refreshButton, isRotating, onClickRefreshBtn, shouldShowRoadEventListModal, toggleShowRoadEventListModal, isSameBusLocation, createNaverMap, shouldGetBusLocationAndFindCampus, shouldShowLocationRefreshButton, handleBusLocation, NavigationBar, MapZoomLevelSetter, get InlineSvg() { return InlineSvg }, Page, BusStopStreetView, RoadEventListModal, PageModal, MapOfNearbyBusStopsBottomSheet }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})